import React, { useState, useEffect, useRef } from 'react';
import { questionService } from '../../services/question.service';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { questionActions, questionTypeActions, questionTopicActions, gradeActions, questionTemplateActions, examActions, notificationActions } from '../../redux/actions';
import ConfirmationPopup from '../Common/ConfirmationPopup';
import {
  MultipleChoiceForm,
  VerbConjugationForm,
  WriteSentencePromptsForm,
  SentenceOrderingForm,
  WriteSentenceForm,
  RewriteSentenceForm,
  ReadingEssayForm,
  ReadingMCQForm,
  FillBlanksWordBankForm,
  FillBlanksMCQForm,
  FindErrorForm,
  MakeQuestionForm,
  TrueFalseForm,
  WordFormConjugationForm,
} from './QuestionForms';
import {
  QuestionsContainer,
  FormWrapper,
  StyledButton,
  HeaderButtonsContainer,
  FilterInput,
  FilterContainer,
  FilterGroup,
  TableContainer,
  StyledTable,
  QuestionContent,
  DifficultyBadge,
  ContentBox,
  ExplanationBox,
  ToggleExplanationButton,
  QuestionCountContainer,
  FormRow,
  TextArea,
  JsonEditor,
  SelectTag,
  TagsContainer,
  SectionLabel,
  StyledSelect,
  WarningButton,
  DropRightContainer,
  DropRightButton,
  DropRightPanel,
  DropRightTagsContainer,
  DropRightTag,
  DangerButton,
  PrimaryButton,
  SecondaryButton,
  SampleJsonEditor,
  SelectedQuestionsPanel,
  PanelHeader,
  PanelTitle,
  ClearAllButton,
  SelectedQuestionItem,
  QuestionInfo,
  RemoveButton,
  FloatingAddButton,
  FloatingDeleteButton,
  ButtonBadge,
  ExamModalContent,
  ExamCard,
  ExamCardTitle,
  ExamCardMeta,
  EmptyState,
  PaginationButton,
  SearchInput,
} from './QuestionsManage.styled';

const QuestionsManage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<any>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For immediate UI update
  const [filterType, setFilterType] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState<string[]>([]);
  const [filterGrades, setFilterGrades] = useState<number[]>([]);
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [loading, setLoading] = useState(true); // Initial page load
  const [fetchingQuestions, setFetchingQuestions] = useState(false); // Fetching questions (pagination, search, filter)
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [showAddToExamModal, setShowAddToExamModal] = useState(false);
  const [draftExams, setDraftExams] = useState<any[]>([]);
  const [showExplanationMap, setShowExplanationMap] = useState<Record<number, boolean>>({});
  // bulk delete UI state
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [lastDeletedBatch, setLastDeletedBatch] = useState<any[] | null>(null);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const UNDO_DURATION = 5000; // ms

  // Handle URL parameters for pre-filled search - run after initial data loaded
  useEffect(() => {
    if (loading) return; // Wait until initial data is loaded
    
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get('search');
    if (searchParam && searchParam !== searchTerm) {
      setSearchInput(searchParam);
      setSearchTerm(searchParam); // Trigger search immediately
      setCurrentPage(1);
    }
  }, [loading, searchTerm]);

  // Add shake animation CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
        20%, 40%, 60%, 80% { transform: translateX(2px); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Refs for click-outside detection
  const difficultyDropdownRef = useRef<HTMLDivElement>(null);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const topicDropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    id: null as number | null,
    question_type_id: '',
    topic_ids: [] as number[], // Changed from question_topic_id to topic_ids (array)
    content: '',
    explanation: '',
    difficulty: '1',
    grade_ids: [] as number[],
  });

  // Topic management states
  const [topicSearchText, setTopicSearchText] = useState('');
  const [isEditingTopics, setIsEditingTopics] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState<number | null>(null);
  const [editingTopicName, setEditingTopicName] = useState('');
  const [topicCounts, setTopicCounts] = useState<Record<number, number>>({});

  const questions = useAppSelector((state: any) => state.questions?.questionsList || []);
  const pagination = useAppSelector((state: any) => state.questions?.pagination || { page: 1, limit: 4, total: 0, totalPages: 0 });
  const questionTypes = useAppSelector((state: any) => state.questionTypes?.questionTypeList || []);
  const questionTopics = useAppSelector((state: any) => state.questionTopics?.questionTopicList || []);
  const grades = useAppSelector((state: any) => state.grades?.gradesList || []);

  // Render question content in Word-like format
  const renderQuestionContent = (question: any) => {
    const typeId = question.question_type_id;
    let content = question.content;
    
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch (e) {
        return <div style={{ fontSize: '13px' }}>{question.content}</div>;
      }
    }

    return (
      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
        {/* Type 1: Multiple Choice */}
        {typeId === 1 && (
          <>
            <div style={{ marginBottom: '6px' }}>{content.text || ''}</div>
            {content.options && (
              <div style={{ fontSize: '12px', color: '#555' }}>
                {content.options.map((opt: string, i: number) => (
                  <div key={i}>
                    {String.fromCharCode(65 + i)}. {opt}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Type 2: Verb Conjugation */}
        {typeId === 2 && (
          <>
            <div>{content.text || ''}</div>
          </>
        )}

        {/* Type 3: Sentence Ordering */}
        {typeId === 3 && (
          <>
            <div>{(content.words || []).join(' / ')}</div>
          </>
        )}

        {/* Type 4: Write Sentence */}
        {typeId === 4 && content.prompts && (
          <>
            {content.prompts.map((prompt: any, i: number) => (
              <div key={i} style={{ marginBottom: '4px' }}>
                {prompt.text}
              </div>
            ))}
          </>
        )}

        {/* Type 5: Rewrite Sentence */}
        {typeId === 5 && (
          <>
            <div>{content.original_sentence || ''}</div>
            {content.instruction && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                → {content.instruction}
              </div>
            )}
          </>
        )}

        {/* Type 6: Reading Essay */}
        {typeId === 6 && (
          <>
            <div style={{ marginBottom: '8px', fontStyle: 'italic' }}>
              {content.passage ? `${content.passage.substring(0, 100)}...` : ''}
            </div>
            {content.questions && (
              <div style={{ fontSize: '12px', color: '#555' }}>
                {content.questions.map((q: any, i: number) => (
                  <div key={i}>• {q.question_text}</div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Type 7: Reading MCQ */}
        {typeId === 7 && (
          <>
            <div style={{ marginBottom: '8px', fontStyle: 'italic' }}>
              {content.passage ? `${content.passage.substring(0, 100)}...` : ''}
            </div>
            {content.questions && (
              <div style={{ fontSize: '12px', color: '#555' }}>
                {content.questions.map((q: any, i: number) => (
                  <div key={i} style={{ marginBottom: '4px' }}>
                    <div>• {q.question_text}</div>
                    {q.options && (
                      <div style={{ marginLeft: '12px' }}>
                        {q.options.map((opt: string, j: number) => (
                          <span key={j} style={{ marginRight: '8px' }}>
                            {String.fromCharCode(65 + j)}. {opt}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Type 8: Fill Blanks Word Bank */}
        {typeId === 8 && (
          <>
            {content.word_bank && (
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                📦 {content.word_bank.join(', ')}
              </div>
            )}
            <div>{content.text || ''}</div>
          </>
        )}

        {/* Type 9: Fill Blanks MCQ */}
        {typeId === 9 && (
          <>
            <div style={{ marginBottom: '8px', fontStyle: 'italic' }}>
              {(content.passage || '').replace(/_{3,}|\.{3,}/g, (() => {
                let idx = 1;
                return () => `(${idx++}) ____`;
              })())}
            </div>
            {content.questions && (
              <div style={{ fontSize: '12px', color: '#555' }}>
                {content.questions.map((q: any, i: number) => (
                  <div key={i} style={{ marginBottom: '4px' }}>
                    <div>Chỗ trống {i + 1}</div>
                    {q.options && q.options.length > 0 && (
                      <div style={{ marginLeft: '12px' }}>
                        {q.options.map((opt: string, j: number) => (
                          <span key={j} style={{ marginRight: '8px' }}>
                            {String.fromCharCode(65 + j)}. {opt}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Type 10: Find Error */}
        {typeId === 10 && (
          <>
            <div>{content.sentence || ''}</div>
          </>
        )}

        {/* Type 11: Make Question */}
        {typeId === 11 && (
          <>
            <div>
              {(() => {
                const sentence = content.sentence || '';
                const underlined = content.underlined || '';
                if (sentence && underlined) {
                  const parts = sentence.split(underlined);
                  return (
                    <>
                      {parts[0]}<u style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{underlined}</u>{parts[1] || ''}
                    </>
                  );
                }
                return sentence;
              })()}
            </div>
          </>
        )}

        {/* Type 12: True/False with multiple statements */}
        {typeId === 12 && (
          <>
            {content.passage && (
              <div style={{ marginBottom: '6px', fontStyle: 'italic' }}>{content.passage}</div>
            )}
            {(content.statements || []).map((s: any, idx: number) => (
              <div key={idx} style={{ fontSize: '12px', color: '#333', marginBottom: 4 }}>
                {idx + 1}) {s.text || ''} → <strong>{s.is_true ? 'True' : 'False'}</strong>
              </div>
            ))}
          </>
        )}

        {/* Type 13: Word Form */}
        {typeId === 13 && (
          <>
            <div>{content.text || ''}</div>
          </>
        )}

        {/* Default: Show raw text or JSON */}
        {![1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 13].includes(typeId) && (
          <div>{content.text || content.passage || JSON.stringify(content)}</div>
        )}
      </div>
    );
  };

  const renderAnswerAndExplanation = (question: any) => {
    const typeId = question.question_type_id;
    let content = question.content;

    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch (e) {
        content = { text: content };
      }
    }

    const items: React.ReactNode[] = [];

    switch (typeId) {
      case 1: // Multiple Choice
        if (content.options && content.correct_index !== undefined) {
          const label = String.fromCharCode(65 + content.correct_index);
          items.push(<div key="a1"><strong>Đáp án:</strong> {label}. {content.options[content.correct_index]}</div>);
        }
        break;
      case 2: // Verb Conjugation
      case 13: // Word Form
        if (content.correct_answer) {
          items.push(<div key="a2"><strong>Đáp án:</strong> {content.correct_answer}</div>);
        }
        break;
      case 3: // Sentence Ordering
        if (content.correct_answers && Array.isArray(content.correct_answers)) {
          content.correct_answers.forEach((ans: string, idx: number) => {
            if (ans) items.push(<div key={`a3-${idx}`}><strong>Đáp án {idx + 1}:</strong> {ans}</div>);
          });
        } else if (content.correct_sentence) {
          // Backward compatibility
          items.push(<div key="a3"><strong>Đáp án:</strong> {content.correct_sentence}</div>);
        }
        break;
      case 4: // Write Sentence
        if (content.prompts) {
          content.prompts.forEach((p: any, idx: number) => {
            if (p.answers && Array.isArray(p.answers)) {
              p.answers.forEach((ans: string, ansIdx: number) => {
                if (ans) items.push(<div key={`a4-${idx}-${ansIdx}`}><strong>Câu {idx + 1}, Đáp án {ansIdx + 1}:</strong> {ans}</div>);
              });
            } else if (p.answer) {
              // Backward compatibility
              items.push(<div key={`a4-${idx}`}><strong>Đáp án {idx + 1}:</strong> {p.answer}</div>);
            }
          });
        }
        break;
      case 5: // Rewrite Sentence
        if (content.correct_answers && Array.isArray(content.correct_answers)) {
          content.correct_answers.forEach((ans: string, idx: number) => {
            if (ans) items.push(<div key={`a5-${idx}`}><strong>Đáp án {idx + 1}:</strong> {ans}</div>);
          });
        } else if (content.correct_answer) {
          // Backward compatibility
          items.push(<div key="a5"><strong>Đáp án:</strong> {content.correct_answer}</div>);
        }
        break;
      case 7: // Reading MCQ
        if (content.questions) {
          content.questions.forEach((q: any, idx: number) => {
            if (q.options && q.correct_index !== undefined) {
              const label = String.fromCharCode(65 + q.correct_index);
              items.push(
                <div key={`a7-${idx}`}>
                  <strong>{String.fromCharCode(97 + idx)})</strong> {label}. {q.options[q.correct_index]}
                </div>
              );
            }
          });
        }
        break;
      case 8: // Fill blanks word bank
        if (content.answers) {
          items.push(<div key="a8"><strong>Đáp án:</strong> {content.answers.join(', ')}</div>);
        }
        break;
      case 9: // Fill blanks MCQ
        if (content.questions) {
          content.questions.forEach((q: any, idx: number) => {
            if (q.options && q.correct_index !== undefined) {
              const label = String.fromCharCode(65 + q.correct_index);
              items.push(
                <div key={`a9-${idx}`}>
                  <strong>Chỗ trống {idx + 1}:</strong> {label}
                </div>
              );
            }
          });
        }
        break;
      case 10: // Find Error
        if (content.error_word || content.correction) {
          items.push(
            <div key="a10">
              {content.error_word && <span style={{ color: '#d32f2f' }}><strong>Lỗi:</strong> {content.error_word}</span>}
              {content.correction && <span style={{ marginLeft: 8, color: '#388e3c' }}><strong>Sửa:</strong> {content.correction}</span>}
            </div>
          );
        }
        break;
      case 11: // Make Question
        if (content.correct_questions && Array.isArray(content.correct_questions)) {
          content.correct_questions.forEach((q: string, idx: number) => {
            if (q) items.push(<div key={`a11-${idx}`}><strong>Câu hỏi đúng {idx + 1}:</strong> {q}</div>);
          });
        } else if (content.correct_question) {
          // Backward compatibility
          items.push(<div key="a11"><strong>Đáp án:</strong> {content.correct_question}</div>);
        }
        break;
      case 12: // True/False (multiple statements)
        if (Array.isArray(content.statements)) {
          content.statements.forEach((s: any, idx: number) => {
            items.push(
              <div key={`a12-${idx}`}><strong>Câu {idx + 1}:</strong> {s.is_true ? 'True' : 'False'}</div>
            );
          });
        }
        break;
      default:
        break;
    }

    if (question.explanation) {
      items.push(
        <div key="exp" style={{ marginTop: '6px' }}>
          <strong>Giải thích:</strong> {question.explanation}
        </div>
      );
    }

    if (items.length === 0) {
      return <div style={{ color: '#777', fontSize: '12px' }}>Không có đáp án/giải thích</div>;
    }

    return <div style={{ fontSize: '13px', lineHeight: '1.6' }}>{items}</div>;
  };

  const toggleExplanation = (id: number) => {
    setShowExplanationMap(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Fetch questions with pagination and filters
  const fetchQuestions = async () => {
    setFetchingQuestions(true);
    const params: any = {
      page: currentPage,
      limit: 20,
    };

    if (searchTerm) params.search = searchTerm;
    if (filterType) params.question_type_id = filterType;
    if (filterTopic) params.question_topic_id = filterTopic;
    
    // Support multiselect difficulty: send comma-separated values or single value
    if (filterDifficulty.length > 0) {
      params.difficulty = filterDifficulty.join(',');
    }
    
    // Support multiselect grades: send comma-separated grade IDs
    if (filterGrades.length > 0) {
      params.grade_ids = filterGrades.join(',');
    }

    console.log('🔍 fetchQuestions params:', params);
    console.log('📊 filterGrades state:', filterGrades);
    console.log('📊 filterDifficulty state:', filterDifficulty);

    await dispatch(questionActions.getAllPaginated(params));
    setFetchingQuestions(false);
  };

  // Initial load: Load metadata then questions
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(questionTypeActions.getAll()),
          dispatch(questionTopicActions.getAll()),
          dispatch(gradeActions.getAll()),
        ]);
        // Load topic counts
        loadTopicCounts();
        // Fetch initial questions after metadata loaded
        await fetchQuestions();
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [dispatch]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 800); // Wait 800ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Click-outside handler for DropRight panels
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (difficultyDropdownRef.current && !difficultyDropdownRef.current.contains(event.target as Node)) {
        setShowDifficultyDropdown(false);
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target as Node)) {
        setShowGradeDropdown(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
      if (topicDropdownRef.current && !topicDropdownRef.current.contains(event.target as Node)) {
        setShowTopicDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch questions when dependencies change
  useEffect(() => {
    if (!loading) {
      fetchQuestions();
    }
  }, [currentPage, searchTerm, filterType, filterTopic, filterDifficulty, filterGrades]);

  // Auto-hide undo toast after countdown and animate progress
  useEffect(() => {
    if (lastDeletedBatch && lastDeletedBatch.length > 0) {
      const startedAt = Date.now();
      setUndoCountdown(UNDO_DURATION);

      const timer = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, UNDO_DURATION - elapsed);
        setUndoCountdown(remaining);
        if (remaining <= 0) {
          setLastDeletedBatch(null);
        }
      }, 100);

      return () => clearInterval(timer);
    }

    setUndoCountdown(0);
    return undefined;
  }, [lastDeletedBatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Khi đổi loại câu hỏi, reset content về empty để form mới parse đúng default structure
    if (name === 'question_type_id' && value !== formData.question_type_id) {
      setFormData({ 
        ...formData, 
        [name]: value,
        content: '' // Reset content để form mới khởi tạo default structure
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toggleQuestionModal = () => {
    if (!questionModalOpen) {
      // Khi mở modal mới (tạo mới), chọn tất cả lớp
      setFormData({
        id: null,
        question_type_id: '',
        topic_ids: [],
        content: '',
        explanation: '',
        difficulty: '1',
        grade_ids: grades.map((g: any) => g.id), // Mặc định chọn tất cả
      });
    }
    setQuestionModalOpen(!questionModalOpen);
    if (questionModalOpen) {
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      question_type_id: '',
      topic_ids: [],
      content: '',
      explanation: '',
      difficulty: '1',
      grade_ids: grades.map((g: any) => g.id), // Mặc định chọn tất cả
    });
  };

  const handleGradeCheckboxChange = (gradeId: number) => {
    const currentGrades = [...formData.grade_ids];
    const isAllSelected = currentGrades.length === grades.length;
    
    // Nếu đang chọn "Tất cả", khi click vào 1 tag cụ thể → chỉ chọn tag đó
    if (isAllSelected) {
      setFormData({
        ...formData,
        grade_ids: [gradeId]
      });
    } else {
      // Logic toggle bình thường
      if (currentGrades.includes(gradeId)) {
        setFormData({
          ...formData,
          grade_ids: currentGrades.filter(id => id !== gradeId)
        });
      } else {
        setFormData({
          ...formData,
          grade_ids: [...currentGrades, gradeId]
        });
      }
    }
  };

  const handleGradeToggle = (gradeId: number) => {
    handleGradeCheckboxChange(gradeId);
  };

  const handleGradeToggleAll = () => {
    if (formData.grade_ids.length === grades.length) {
      // Nếu đã chọn tất cả, bỏ chọn tất cả
      setFormData({ ...formData, grade_ids: [] });
    } else {
      // Chọn tất cả
      setFormData({ ...formData, grade_ids: grades.map((g: any) => g.id) });
    }
  };

  // Topic toggle handlers (similar to grade toggle)
  const handleTopicToggle = (topicId: number) => {
    const currentTopics = [...formData.topic_ids];
    const isAllSelected = currentTopics.length === questionTopics.length;
    
    // Nếu đang chọn "Tất cả", khi click vào 1 tag cụ thể → chỉ chọn tag đó
    if (isAllSelected) {
      setFormData({
        ...formData,
        topic_ids: [topicId]
      });
    } else {
      // Logic toggle bình thường
      if (currentTopics.includes(topicId)) {
        // Remove topic
        setFormData({
          ...formData,
          topic_ids: currentTopics.filter(id => id !== topicId)
        });
      } else {
        // Add topic
        setFormData({
          ...formData,
          topic_ids: [...currentTopics, topicId]
        });
      }
    }
  };

  const handleTopicToggleAll = () => {
    if (formData.topic_ids.length === questionTopics.length) {
      // Nếu đã chọn tất cả, bỏ chọn tất cả
      setFormData({ ...formData, topic_ids: [] });
    } else {
      // Chọn tất cả
      setFormData({ ...formData, topic_ids: questionTopics.map((t: any) => t.id) });
    }
  };

  // Topic management handlers
  const loadTopicCounts = async () => {
    try {
      const counts = await dispatch(questionTopicActions.getQuestionCounts());
      const countsMap: Record<number, number> = {};
      counts.forEach((item: any) => {
        countsMap[item.topic_id] = parseInt(item.count);
      });
      setTopicCounts(countsMap);
    } catch (error) {
      console.error('Error loading topic counts:', error);
    }
  };

  const handleAddTopic = async () => {
    const name = prompt('Nhập tên chủ đề mới:');
    if (name && name.trim()) {
      try {
        await dispatch(questionTopicActions.create(name.trim()));
        await loadTopicCounts();
      } catch (error) {
        // Error already shown by action
      }
    }
  };

  const handleStartEditTopics = () => {
    setIsEditingTopics(true);
    setEditingTopicId(null);
    setEditingTopicName('');
  };

  const handleCancelEditTopics = () => {
    setIsEditingTopics(false);
    setEditingTopicId(null);
    setEditingTopicName('');
  };

  const handleEditTopicClick = (topic: any) => {
    setEditingTopicId(topic.id);
    setEditingTopicName(topic.vn_name);
  };

  const handleSaveTopicEdit = async (topicId: number) => {
    if (editingTopicName.trim() && editingTopicName.trim() !== questionTopics.find((t: any) => t.id === topicId)?.vn_name) {
      try {
        await dispatch(questionTopicActions.update(topicId, editingTopicName.trim()));
        setEditingTopicId(null);
        setEditingTopicName('');
      } catch (error) {
        // Error already shown by action
      }
    } else {
      setEditingTopicId(null);
      setEditingTopicName('');
    }
  };

  const handleDeleteTopic = async (topic: any) => {
    const count = topicCounts[topic.id] || 0;
    const confirmed = window.confirm(
      `Bạn có chắc muốn xóa chủ đề "${topic.vn_name}"?\n\n` +
      `Hiện có ${count} câu hỏi thuộc chủ đề này.\n` +
      `Các câu hỏi sẽ bị gỡ chủ đề này (không bị xóa câu hỏi).`
    );
    
    if (confirmed) {
      try {
        await dispatch(questionTopicActions.deleteTopic(topic.id));
        await loadTopicCounts();
        // Refresh questions to update the topic list
        await fetchQuestions();
      } catch (error) {
        // Error already shown by action
      }
    }
  };

  const getFilteredTopics = () => {
    if (!topicSearchText.trim()) return questionTopics;
    
    const searchLower = topicSearchText.toLowerCase().trim();
    return questionTopics.filter((topic: any) => 
      topic.vn_name.toLowerCase().includes(searchLower)
    );
  };

  const handleDifficultyToggle = (difficulty: string) => {
    setFormData({ ...formData, difficulty });
  };

  // Render dynamic form based on question type
  const renderQuestionContentForm = () => {
    const selectedType = questionTypes.find((t: any) => t.id === parseInt(formData.question_type_id));
    
    if (!selectedType) {
      return (
        <FormGroup>
          <Label for="content">Nội dung câu hỏi (JSON) *</Label>
          <JsonEditor
            name="content"
            id="content"
            value={formData.content}
            onChange={handleInputChange}
            placeholder='{"text": "What is your name?"}'
            required
          />
          <small className="form-text text-muted">
            Chọn loại câu hỏi để sử dụng form nhập liệu
          </small>
        </FormGroup>
      );
    }

    switch (selectedType.id) {
      case 1: // Trắc nghiệm A, B, C, D
        return <MultipleChoiceForm formData={formData} onChange={setFormData} />;
      
      case 2: // Chia động từ trong ngoặc
        return <VerbConjugationForm formData={formData} onChange={setFormData} />;
      
      case 3: // Sắp xếp các từ thành câu hoàn chỉnh
        return <SentenceOrderingForm formData={formData} onChange={setFormData} />;
      
      case 4: // Viết câu từ các từ gợi ý (prompts structure)
        return <WriteSentencePromptsForm formData={formData} onChange={setFormData} />;
      
      case 5: // Viết lại câu sao cho nghĩa không đổi
        return <RewriteSentenceForm formData={formData} onChange={setFormData} />;
      
      case 6: // Đọc đoạn văn và trả lời câu hỏi TỰ LUẬN
        return <ReadingEssayForm formData={formData} onChange={setFormData} />;
      
      case 7: // Đọc đoạn văn và trả lời câu hỏi TRẮC NGHIỆM
        return <ReadingMCQForm formData={formData} onChange={setFormData} />;
      
      case 8: // Điền từ (các từ cho sẵn) vào đoạn văn bị khuyết
        return <FillBlanksWordBankForm formData={formData} onChange={setFormData} />;
      
      case 9: // Điền từ (chọn A, B, C, D) để điền vào đoạn văn bị khuyết
        return <FillBlanksMCQForm formData={formData} onChange={setFormData} />;
      
      case 10: // Tìm lỗi sai (trong A,B,C,D) và sửa lại cho đúng
        return <FindErrorForm formData={formData} onChange={setFormData} />;
      
      case 11: // Đặt câu hỏi cho phần gạch chân
        return <MakeQuestionForm formData={formData} onChange={setFormData} />;
      
      case 12: // Đọc đoạn văn, điền True (T) hoặc False (F)
        return <TrueFalseForm formData={formData} onChange={setFormData} />;
      
      case 13: // Điền từ loại
        return <WordFormConjugationForm formData={formData} onChange={setFormData} />;
      
      default:
        // Fallback: JSON Editor for types without custom form
        return (
          <FormGroup>
            <Label for="content">Nội dung câu hỏi (JSON) *</Label>
            <JsonEditor
              name="content"
              id="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder='{"text": "..."}'
              required
            />
            <small className="form-text text-warning">
              ⚠️ Loại câu hỏi này chưa có form nhập liệu. Vui lòng nhập JSON.
            </small>
          </FormGroup>
        );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse content as JSON if possible
    let parsedContent;
    try {
      parsedContent = JSON.parse(formData.content);
    } catch {
      parsedContent = formData.content; // Keep as string if not valid JSON
    }

    // Validation for typeId=3 (SentenceOrdering): correct_answers is required
    if (parseInt(formData.question_type_id) === 3) {
      const correctAnswers = parsedContent?.correct_answers || [];
      const hasValidAnswer = correctAnswers.some((ans: string) => ans && ans.trim() !== '');
      if (!hasValidAnswer) {
        alert('Vui lòng nhập ít nhất 1 "Đáp án đúng" cho câu hỏi sắp xếp câu!');
        return;
      }
    }

    // Validation for typeId=4 (WriteSentencePrompts): each prompt must have at least 1 answer
    if (parseInt(formData.question_type_id) === 4) {
      const prompts = parsedContent?.prompts || [];
      if (prompts.length === 0) {
        alert('Vui lòng thêm ít nhất 1 câu với từ gợi ý!');
        return;
      }
      for (let i = 0; i < prompts.length; i++) {
        const answers = prompts[i]?.answers || [];
        const hasValidAnswer = answers.some((ans: string) => ans && ans.trim() !== '');
        if (!hasValidAnswer) {
          alert(`Vui lòng nhập ít nhất 1 đáp án đúng cho câu ${i + 1}!`);
          return;
        }
      }
    }

    // Validation for typeId=5 (RewriteSentence): correct_answers is required
    if (parseInt(formData.question_type_id) === 5) {
      const correctAnswers = parsedContent?.correct_answers || [];
      const hasValidAnswer = correctAnswers.some((ans: string) => ans && ans.trim() !== '');
      if (!hasValidAnswer) {
        alert('Vui lòng nhập ít nhất 1 "Câu viết lại đúng" cho câu hỏi viết lại câu!');
        return;
      }
    }

    // Validation for typeId=11 (MakeQuestion): correct_questions is required
    if (parseInt(formData.question_type_id) === 11) {
      const correctQuestions = parsedContent?.correct_questions || [];
      const hasValidQuestion = correctQuestions.some((q: string) => q && q.trim() !== '');
      if (!hasValidQuestion) {
        alert('Vui lòng nhập ít nhất 1 "Câu hỏi đúng" cho câu hỏi đặt câu hỏi!');
        return;
      }
    }

    const questionData = {
      ...formData,
      content: parsedContent,
      question_type_id: parseInt(formData.question_type_id),
      topic_ids: formData.topic_ids, // Send array of topic IDs
      difficulty: parseInt(formData.difficulty),
      grade_ids: formData.grade_ids, // Send array of grade IDs
    };

    if (formData.id) {
      await dispatch(questionActions.updateQuestion(questionData));
    } else {
      await dispatch(questionActions.createQuestion(questionData));
    }
    toggleQuestionModal();
    fetchQuestions(); // Refresh list after create/update
  };

  const handleEdit = (question: any) => {
    const gradeIds = question.grades ? question.grades.map((g: any) => g.id) : [];
    const topicIds = question.topics ? question.topics.map((t: any) => t.id) : [];
    // Parse content and ensure required fields for typeId=9,10
    let parsedContent;
    try {
      parsedContent = typeof question.content === 'object' ? question.content : JSON.parse(question.content);
    } catch {
      parsedContent = question.content;
    }

    // Type 9: FillBlanksMCQ
    if (question.question_type_id === 9) {
      if (!parsedContent || typeof parsedContent !== 'object') {
        parsedContent = { passage: '', questions: [] };
      } else {
        if (!parsedContent.passage) parsedContent.passage = '';
        if (!Array.isArray(parsedContent.questions)) parsedContent.questions = [];
      }
    }
    // Type 10: FindErrorForm
    if (question.question_type_id === 10) {
      if (!parsedContent || typeof parsedContent !== 'object') {
        parsedContent = { sentence: '', options: [], wrong_index: -1, correct_answer: '' };
      } else {
        if (!parsedContent.sentence) parsedContent.sentence = '';
        if (!Array.isArray(parsedContent.options)) parsedContent.options = [];
        if (typeof parsedContent.wrong_index !== 'number') parsedContent.wrong_index = -1;
        if (!parsedContent.correct_answer) parsedContent.correct_answer = '';
      }
    }
    // Type 12: True/False multi statements
    if (question.question_type_id === 12) {
      if (!parsedContent || typeof parsedContent !== 'object') {
        parsedContent = { passage: '', statements: [{ text: '', is_true: true }] };
      } else {
        if (!parsedContent.passage) parsedContent.passage = '';
        if (!Array.isArray(parsedContent.statements)) {
          // Backward compatibility: legacy single statement
          const legacyStatement = parsedContent.statement || parsedContent.text || '';
          const legacyIsTrue = parsedContent.correct_answer ?? parsedContent.is_true ?? true;
          parsedContent.statements = [{ text: legacyStatement, is_true: Boolean(legacyIsTrue) }];
        } else if (parsedContent.statements.length === 0) {
          parsedContent.statements = [{ text: '', is_true: true }];
        } else {
          parsedContent.statements = parsedContent.statements.map((s: any) => ({
            text: s?.text || s?.statement || '',
            is_true: Boolean(s?.is_true ?? s?.correct_answer ?? true),
          }));
        }
      }
    }

    setFormData({
      id: question.id,
      question_type_id: String(question.question_type_id),
      topic_ids: topicIds,
      content: JSON.stringify(parsedContent),
      explanation: question.explanation || '',
      difficulty: String(question.difficulty),
      grade_ids: gradeIds,
    });
    setQuestionModalOpen(true);
  };

  const handleDeleteClick = (question: any) => {
    setQuestionToDelete(question);
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (questionToDelete) {
      await dispatch(questionActions.deleteQuestion(questionToDelete.id));
      fetchQuestions(); // Refresh list after delete
    }
    setShowDeletePopup(false);
    setQuestionToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeletePopup(false);
    setQuestionToDelete(null);
  };

  const handleBulkDeleteClick = () => {
    setShowBulkDeleteModal(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      setIsBulkDeleting(true);
      // cache selected questions for undo
      const selectedQuestions = questions.filter((q: any) => selectedQuestionIds.includes(q.id));
      setLastDeletedBatch(selectedQuestions);
      await dispatch(questionActions.deleteMultipleQuestions(selectedQuestionIds));
      setShowBulkDeleteModal(false);
      setSelectedQuestionIds([]);
      fetchQuestions(); // Refresh list after delete
      dispatch(notificationActions.addNotification('Xóa câu hỏi thành công', 'SUCCESS'));
    } catch (error) {
      dispatch(notificationActions.addNotification('Lỗi khi xóa câu hỏi', 'DANGER'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDeleteCancel = () => {
    setShowBulkDeleteModal(false);
  };

  const handleUndoLastDelete = async () => {
    if (!lastDeletedBatch || lastDeletedBatch.length === 0) return;
    try {
      // Recreate questions in bulk via backend helper
      await questionService.recreateMultiple(lastDeletedBatch.map((q: any) => ({
        id: q.id,
        question_type_id: q.question_type_id,
        content: q.content,
        explanation: q.explanation,
        difficulty: q.difficulty,
        // optional associations if available on item
        topic_ids: (q.topics || []).map((t: any) => t.id),
        grade_ids: (q.grades || []).map((g: any) => g.id),
      })));
      setLastDeletedBatch(null);
      fetchQuestions();
      dispatch(notificationActions.addNotification('Khôi phục câu hỏi thành công', 'SUCCESS'));
    } catch (error) {
      dispatch(notificationActions.addNotification('Không thể khôi phục câu hỏi', 'DANGER'));
    }
  };

  // Handle search input (immediate UI update, debounced API call)
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value); // Update input immediately
  };

  // Handle multiselect filter changes
  const toggleDifficultyFilter = (value: string) => {
    if (value === '') {
      // "Tất cả" clicked - clear all filters
      setFilterDifficulty([]);
    } else {
      // Toggle individual difficulty
      if (filterDifficulty.includes(value)) {
        setFilterDifficulty(filterDifficulty.filter(d => d !== value));
      } else {
        setFilterDifficulty([...filterDifficulty, value]);
      }
    }
    setCurrentPage(1);
  };

  const toggleGradeFilter = (gradeId: number) => {
    console.log('🎯 toggleGradeFilter called with gradeId:', gradeId);
    console.log('📊 Current filterGrades:', filterGrades);
    
    if (gradeId === -1) {
      // "Tất cả" clicked - clear all filters
      console.log('✅ Clearing all grade filters');
      setFilterGrades([]);
    } else {
      // Toggle individual grade
      if (filterGrades.includes(gradeId)) {
        console.log('➖ Removing grade:', gradeId);
        setFilterGrades(filterGrades.filter(g => g !== gradeId));
      } else {
        console.log('➕ Adding grade:', gradeId);
        setFilterGrades([...filterGrades, gradeId]);
      }
    }
    setCurrentPage(1);
  };

  // Handle filter changes (for simple filters)
  const handleFilterChange = (filterName: string, value: string) => {
    if (filterName === 'type') setFilterType(value);
    if (filterName === 'topic') setFilterTopic(value);
    if (filterName === 'difficulty') setFilterDifficulty([value]); // Legacy support - convert to array
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Checkbox handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedQuestionIds(questions.map((q: any) => q.id));
    } else {
      setSelectedQuestionIds([]);
    }
  };

  const handleSelectQuestion = (questionId: number) => {
    if (selectedQuestionIds.includes(questionId)) {
      setSelectedQuestionIds(selectedQuestionIds.filter(id => id !== questionId));
    } else {
      setSelectedQuestionIds([...selectedQuestionIds, questionId]);
    }
  };

  const handleRemoveSelected = (questionId: number) => {
    setSelectedQuestionIds(selectedQuestionIds.filter(id => id !== questionId));
  };

  const handleClearAllSelected = () => {
    setSelectedQuestionIds([]);
  };

  const handleOpenAddToExamModal = async () => {
    // Fetch draft exams
    console.log('🔍 Fetching draft exams with params:', { page: 1, limit: 100, status: 'draft' });
    const result: any = await dispatch(examActions.getAllPaginated({ page: 1, limit: 100, status: 'draft' }));
    console.log('📦 Raw result from action:', result);
    console.log('📊 result.data:', result?.data);
    console.log('📊 result.data length:', result?.data?.length);
    
    if (result?.data && Array.isArray(result.data)) {
      console.log('✅ Draft exams found:', result.data.length);
      console.log('📝 First exam status:', result.data[0]?.status);
      setDraftExams(result.data);
    } else {
      console.log('❌ No data or not array. Result structure:', Object.keys(result || {}));
      setDraftExams([]);
    }
    setShowAddToExamModal(true);
  };

  const handleAddToExam = async (examId: number, examTitle: string) => {
    try {
      await dispatch(examActions.addQuestions(examId, {
        method: 'manual',
        question_ids: selectedQuestionIds,
      }));
      
      // Show success notification using notification system
      dispatch(notificationActions.addNotification(
        `Đã thêm ${selectedQuestionIds.length} câu hỏi vào đề "${examTitle}" thành công!`,
        'SUCCESS'
      ));
      
      // Clear selection and close modal
      setSelectedQuestionIds([]);
      setShowAddToExamModal(false);
    } catch (error) {
      dispatch(notificationActions.addNotification(
        'Có lỗi xảy ra khi thêm câu hỏi vào đề thi!',
        'DANGER'
      ));
    }
  };

  const isDisabled = !formData.question_type_id || !formData.content;

  // Loại bỏ loading state - chỉ dùng FadeIn animation
  return (
    <QuestionsContainer>
      <h2>Quản Lý Ngân Hàng Câu Hỏi</h2>

      <HeaderButtonsContainer>
        <div className="buttons-and-search">
          <StyledButton color="primary" onClick={toggleQuestionModal}>
            + Thêm Câu Hỏi Mới
          </StyledButton>
        </div>

        <div className="filters-row">
          {/* DropRight for Type */}
          <FilterGroup>
            <label>Dạng bài:</label>
            <DropRightContainer ref={typeDropdownRef}>
              <DropRightButton onClick={() => setShowTypeDropdown(!showTypeDropdown)}>
                {filterType ? (
                  questionTypes.find((t: any) => t.id === parseInt(filterType))?.type_name || `Tất cả (${questionTypes.length})`
                ) : (
                  `Tất cả (${questionTypes.length})`
                )}
              </DropRightButton>
              <DropRightPanel isOpen={showTypeDropdown}>
                <DropRightTagsContainer>
                  <DropRightTag
                    isActive={filterType === ''}
                    onClick={() => {
                      handleFilterChange('type', '');
                      setShowTypeDropdown(false);
                    }}
                  >
                    Tất cả ({questionTypes.length})
                  </DropRightTag>
                  {questionTypes.map((type: any) => (
                    <DropRightTag
                      key={type.id}
                      isActive={filterType === String(type.id)}
                      onClick={() => {
                        handleFilterChange('type', String(type.id));
                        setShowTypeDropdown(false);
                      }}
                    >
                      [{type.id}] {type.type_name}
                    </DropRightTag>
                  ))}
                </DropRightTagsContainer>
              </DropRightPanel>
            </DropRightContainer>
          </FilterGroup>

          {/* DropRight for Topic */}
          <FilterGroup>
            <label>Chủ đề:</label>
            <DropRightContainer ref={topicDropdownRef}>
              <DropRightButton onClick={() => setShowTopicDropdown(!showTopicDropdown)}>
                {filterTopic ? (
                  questionTopics.find((t: any) => t.id === parseInt(filterTopic))?.vn_name || `Tất cả (${questionTopics.length})`
                ) : (
                  `Tất cả (${questionTopics.length})`
                )}
              </DropRightButton>
              <DropRightPanel isOpen={showTopicDropdown}>
                <DropRightTagsContainer>
                  <DropRightTag
                    isActive={filterTopic === ''}
                    onClick={() => {
                      handleFilterChange('topic', '');
                      setShowTopicDropdown(false);
                    }}
                  >
                    Tất cả ({questionTopics.length})
                  </DropRightTag>
                  {questionTopics.map((topic: any) => (
                    <DropRightTag
                      key={topic.id}
                      isActive={filterTopic === String(topic.id)}
                      onClick={() => {
                        handleFilterChange('topic', String(topic.id));
                        setShowTopicDropdown(false);
                      }}
                    >
                      [{topic.id}] {topic.vn_name}
                    </DropRightTag>
                  ))}
                </DropRightTagsContainer>
              </DropRightPanel>
            </DropRightContainer>
          </FilterGroup>

          {/* DropRight for Difficulty */}
          <FilterGroup>
            <label>Độ khó:</label>
            <DropRightContainer ref={difficultyDropdownRef}>
              <DropRightButton onClick={() => setShowDifficultyDropdown(!showDifficultyDropdown)}>
                {filterDifficulty.length === 0 
                  ? 'Tất cả (3)' 
                  : `${filterDifficulty.length} đã chọn`
                }
              </DropRightButton>
              <DropRightPanel isOpen={showDifficultyDropdown}>
                <DropRightTagsContainer>
                  <DropRightTag
                    isActive={filterDifficulty.length === 0}
                    onClick={() => toggleDifficultyFilter('')}
                  >
                    Tất cả (3)
                  </DropRightTag>
                  <DropRightTag
                    isActive={filterDifficulty.includes('1')}
                    onClick={() => toggleDifficultyFilter('1')}
                  >
                    😊 Dễ
                  </DropRightTag>
                  <DropRightTag
                    isActive={filterDifficulty.includes('2')}
                    onClick={() => toggleDifficultyFilter('2')}
                  >
                    😐 Trung bình
                  </DropRightTag>
                  <DropRightTag
                    isActive={filterDifficulty.includes('3')}
                    onClick={() => toggleDifficultyFilter('3')}
                  >
                    😤 Khó
                  </DropRightTag>
                </DropRightTagsContainer>
              </DropRightPanel>
            </DropRightContainer>
          </FilterGroup>

          {/* DropRight for Grades */}
          <FilterGroup>
            <label>Lớp:</label>
            <DropRightContainer ref={gradeDropdownRef}>
              <DropRightButton onClick={() => setShowGradeDropdown(!showGradeDropdown)}>
                {filterGrades.length === 0 
                  ? `Tất cả (${grades.length})` 
                  : `${filterGrades.length} đã chọn`
                }
              </DropRightButton>
              <DropRightPanel isOpen={showGradeDropdown}>
                <DropRightTagsContainer>
                  <DropRightTag
                    isActive={filterGrades.length === 0}
                    onClick={() => toggleGradeFilter(-1)}
                  >
                    Tất cả ({grades.length})
                  </DropRightTag>
                  {grades.map((grade: any) => (
                    <DropRightTag
                      key={grade.id}
                      isActive={filterGrades.includes(grade.id)}
                      onClick={() => toggleGradeFilter(grade.id)}
                    >
                      {grade.name}
                    </DropRightTag>
                  ))}
                </DropRightTagsContainer>
              </DropRightPanel>
            </DropRightContainer>
          </FilterGroup>
        </div>
      </HeaderButtonsContainer>

      {/* Question Count and Search - Moved below filters */}
      <QuestionCountContainer>
        <div className="search-wrapper">
          <SearchInput
            type="text"
            placeholder="🔍 Tìm kiếm theo ID, Dạng bài, Chủ đề hoặc Nội dung..."
            value={searchInput}
            onChange={handleSearchChange}
          />
          {searchInput !== searchTerm && !fetchingQuestions && (
            <span style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '11px',
              color: '#aaa',
              fontStyle: 'italic',
              pointerEvents: 'none'
            }}>
              ⌨️ Đang nhập...
            </span>
          )}
          {fetchingQuestions && searchInput && (
            <span style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '11px',
              color: '#007bff',
              fontWeight: 'bold',
              pointerEvents: 'none'
            }}>
              🔍 Tìm...
            </span>
          )}
        </div>

        <div className="pagination-info-group">
          <div className="pagination-buttons">
            <PaginationButton 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              ← Trước
            </PaginationButton>
          </div>
          
          <div className="total-info">
            Trang {pagination.page}/{pagination.totalPages || 1} • {pagination.total} câu
          </div>

          <div className="pagination-buttons">
            <PaginationButton 
              disabled={currentPage === pagination.totalPages || pagination.totalPages === 0}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Sau →
            </PaginationButton>
          </div>
        </div>
      </QuestionCountContainer>

      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <input
                  type="checkbox"
                  checked={selectedQuestionIds.length === questions.length && questions.length > 0}
                  onChange={handleSelectAll}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
              </th>
              <th style={{ width: '50px' }}>ID</th>
              <th style={{ width: '120px' }}>Dạng bài</th>
              <th style={{ width: '120px' }}>Chủ đề</th>
              <th>Nội dung</th>
              <th style={{ width: '80px' }}>Độ khó</th>
              <th style={{ width: '150px' }}>Lớp</th>
              <th style={{ width: '150px' }}>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {/* Loại bỏ fetchingQuestions loading - FadeIn animation đủ mượt */}
            {questions.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Không có câu hỏi nào
                </td>
              </tr>
            ) : (
              questions.map((question: any) => {
                const questionType = questionTypes.find((t: any) => t.id === question.question_type_id);
                const topics = question.topics || [];

                return (
                  <tr key={question.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedQuestionIds.includes(question.id)}
                        onChange={() => handleSelectQuestion(question.id)}
                        style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                      />
                    </td>
                    <td>{question.id}</td>
                    <td>{questionType?.type_name || 'N/A'}</td>
                    <td>
                      {topics.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {topics.map((topic: any) => {
                            const count = topicCounts[topic.id] || 0;
                            return (
                              <span
                                key={topic.id}
                                style={{
                                  background: '#17a2b8',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {topic.vn_name} ({count})
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      <QuestionContent>
                        <ContentBox>
                          {renderQuestionContent(question)}
                        </ContentBox>

                        <ToggleExplanationButton onClick={() => toggleExplanation(question.id)}>
                          {showExplanationMap[question.id] ? 'Ẩn giải thích' : 'Hiện giải thích'}
                        </ToggleExplanationButton>

                        {showExplanationMap[question.id] && (
                          <ExplanationBox>
                            {renderAnswerAndExplanation(question)}
                          </ExplanationBox>
                        )}
                      </QuestionContent>
                    </td>
                  <td>
                    <DifficultyBadge difficulty={question.difficulty}>
                      {question.difficulty === 1 ? 'Dễ' : question.difficulty === 2 ? 'TB' : 'Khó'}
                    </DifficultyBadge>
                  </td>
                  <td>
                    {question.grades && question.grades.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {question.grades.map((grade: any) => (
                          <span 
                            key={grade.id} 
                            style={{ 
                              padding: '2px 8px', 
                              backgroundColor: '#e3f2fd', 
                              borderRadius: '12px', 
                              fontSize: '12px',
                              border: '1px solid #90caf9'
                            }}
                          >
                            {grade.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>N/A</span>
                    )}
                  </td>
                  <td className="action-buttons">
                    <WarningButton onClick={() => handleEdit(question)}>
                      Sửa
                    </WarningButton>{' '}
                    <DangerButton onClick={() => handleDeleteClick(question)}>
                      Xóa
                    </DangerButton>
                  </td>
                </tr>
              );
            })
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Modal for Create/Edit */}
      <Modal isOpen={questionModalOpen} toggle={toggleQuestionModal} size="lg">
        <ModalHeader toggle={toggleQuestionModal}>
          {formData.id ? 'Chỉnh Sửa Câu Hỏi' : 'Thêm Câu Hỏi Mới'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            {/* Loại câu hỏi - dòng riêng */}
            <FormGroup style={{ marginBottom: '20px' }}>
              <Label for="question_type_id">Loại câu hỏi *</Label>
              <StyledSelect
                name="question_type_id"
                id="question_type_id"
                value={formData.question_type_id}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Chọn loại --</option>
                {questionTypes.map((type: any) => (
                  <option key={type.id} value={type.id}>[{type.id}] {type.type_name}</option>
                ))}
              </StyledSelect>
            </FormGroup>

            {/* Chủ đề - dạng tags (multiple selection) */}
            <FormGroup style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <SectionLabel style={{ marginBottom: 0 }}>Chủ đề (có thể chọn nhiều)</SectionLabel>
                
                {/* Search box */}
                <Input
                  type="text"
                  placeholder="🔍 Tìm chủ đề..."
                  value={topicSearchText}
                  onChange={(e) => setTopicSearchText(e.target.value)}
                  style={{ width: '200px', fontSize: '13px', padding: '4px 8px' }}
                />
                
                {/* Add button */}
                <Button 
                  color="success" 
                  size="sm" 
                  onClick={handleAddTopic}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  ➕ Thêm
                </Button>
                
                {/* Edit button */}
                {!isEditingTopics ? (
                  <Button 
                    color="warning" 
                    size="sm" 
                    onClick={handleStartEditTopics}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    ✏️ Sửa
                  </Button>
                ) : (
                  <Button 
                    color="secondary" 
                    size="sm" 
                    onClick={handleCancelEditTopics}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    ✅ Xong
                  </Button>
                )}
              </div>

              {/* Tags container with scroll and max height */}
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '10px',
                backgroundColor: '#fafafa'
              }}>
                <TagsContainer>
                  <SelectTag
                    isActive={formData.topic_ids.length === questionTopics.length && questionTopics.length > 0}
                    onClick={handleTopicToggleAll}
                  >
                    Tất cả
                  </SelectTag>
                  {getFilteredTopics().map((topic: any) => {
                    const isAllSelected = formData.topic_ids.length === questionTopics.length && questionTopics.length > 0;
                    const isThisTopicSelected = formData.topic_ids.includes(topic.id);
                    const count = topicCounts[topic.id] || 0;
                    
                    return (
                      <div 
                        key={topic.id} 
                        style={{ 
                          position: 'relative',
                          display: 'inline-block',
                          animation: isEditingTopics ? 'shake 0.5s infinite' : 'none'
                        }}
                      >
                        {editingTopicId === topic.id ? (
                          <Input
                            type="text"
                            value={editingTopicName}
                            onChange={(e) => setEditingTopicName(e.target.value)}
                            onBlur={() => handleSaveTopicEdit(topic.id)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveTopicEdit(topic.id);
                              }
                            }}
                            autoFocus
                            style={{
                              width: '150px',
                              fontSize: '13px',
                              padding: '4px 8px',
                              display: 'inline-block'
                            }}
                          />
                        ) : (
                          <SelectTag
                            isActive={!isAllSelected && isThisTopicSelected}
                            onClick={() => {
                              if (isEditingTopics) {
                                handleEditTopicClick(topic);
                              } else {
                                handleTopicToggle(topic.id);
                              }
                            }}
                            style={{ paddingRight: isEditingTopics ? '30px' : '12px' }}
                          >
                            {topic.vn_name} ({count})
                          </SelectTag>
                        )}
                        
                        {isEditingTopics && editingTopicId !== topic.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTopic(topic);
                            }}
                            style={{
                              position: 'absolute',
                              top: '-5px',
                              right: '-5px',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              border: 'none',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </TagsContainer>
              </div>
              
              <small style={{ color: '#666', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                💡 Một câu hỏi có thể thuộc nhiều chủ đề (VD: vừa Thì hiện tại đơn, vừa Giới từ) | 
                Hiển thị: {getFilteredTopics().length}/{questionTopics.length} chủ đề
              </small>
            </FormGroup>

            {/* Lớp áp dụng - dạng tags */}
            <FormGroup style={{ marginBottom: '20px' }}>
              <SectionLabel>Lớp áp dụng</SectionLabel>
              <TagsContainer>
                <SelectTag
                  isActive={formData.grade_ids.length === grades.length && grades.length > 0}
                  onClick={handleGradeToggleAll}
                >
                  Tất cả
                </SelectTag>
                {[...grades]
                  .sort((a: any, b: any) => {
                    // Sắp xếp theo tên: Lớp 1, Lớp 2, ..., Lớp 9
                    const aNum = parseInt(a.name.replace(/\D/g, ''));
                    const bNum = parseInt(b.name.replace(/\D/g, ''));
                    return aNum - bNum;
                  })
                  .map((grade: any) => {
                    // Khi "Tất cả" được chọn, UI hiển thị các tag khác OFF
                    const isAllSelected = formData.grade_ids.length === grades.length && grades.length > 0;
                    const isThisGradeSelected = formData.grade_ids.includes(grade.id);
                    
                    return (
                      <SelectTag
                        key={grade.id}
                        isActive={isAllSelected ? false : isThisGradeSelected}
                        onClick={() => handleGradeToggle(grade.id)}
                      >
                        {grade.name}
                      </SelectTag>
                    );
                  })}
              </TagsContainer>
            </FormGroup>

            {/* Độ khó - dạng tags */}
            <FormGroup style={{ marginBottom: '20px' }}>
              <SectionLabel>Độ khó *</SectionLabel>
              <TagsContainer>
                <SelectTag
                  isActive={formData.difficulty === '1'}
                  onClick={() => handleDifficultyToggle('1')}
                >
                  😊 Dễ
                </SelectTag>
                <SelectTag
                  isActive={formData.difficulty === '2'}
                  onClick={() => handleDifficultyToggle('2')}
                >
                  😐 Trung bình
                </SelectTag>
                <SelectTag
                  isActive={formData.difficulty === '3'}
                  onClick={() => handleDifficultyToggle('3')}
                >
                  😤 Khó
                </SelectTag>
              </TagsContainer>
            </FormGroup>

            {/* Nội dung câu hỏi - Dynamic Form theo loại */}
            {renderQuestionContentForm()}

            <FormGroup>
              <Label for="explanation">Giải thích</Label>
              <TextArea
                name="explanation"
                id="explanation"
                value={formData.explanation}
                onChange={handleInputChange}
                placeholder="Giải thích đáp án..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton onClick={handleSubmit} disabled={isDisabled}>
            {formData.id ? 'Cập Nhật' : 'Tạo Mới'}
          </PrimaryButton>
          <SecondaryButton onClick={toggleQuestionModal}>
            Hủy
          </SecondaryButton>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Popup */}
      <ConfirmationPopup
        isOpen={showDeletePopup}
        title="Xác nhận xóa"
        message={`Bạn có chắc chắn muốn xóa câu hỏi ID ${questionToDelete?.id}?`}
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={showBulkDeleteModal} toggle={handleBulkDeleteCancel} size="md">
        <ModalHeader toggle={handleBulkDeleteCancel}>
          ⚠️ Xác nhận xóa câu hỏi
        </ModalHeader>
        <ModalBody>
          <div style={{ padding: '20px 0' }}>
            <p style={{ fontSize: '16px', marginBottom: '16px' }}>
              Bạn có chắc chắn muốn xóa <strong>{selectedQuestionIds.length}</strong> câu hỏi sau không?
            </p>
            <div style={{ 
              padding: '12px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              maxHeight: '200px',
              overflowY: 'auto',
              fontSize: '14px',
              color: '#666'
            }}>
              <strong>Danh sách ID:</strong> {selectedQuestionIds.join(', ')}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <SecondaryButton onClick={handleBulkDeleteCancel} disabled={isBulkDeleting}>
            Hủy
          </SecondaryButton>
          <DangerButton onClick={handleBulkDeleteConfirm} disabled={isBulkDeleting}>
            Xóa {selectedQuestionIds.length} câu hỏi
          </DangerButton>
        </ModalFooter>
      </Modal>

      {/* Selected Questions Panel */}
      {selectedQuestionIds.length > 0 && (
        <SelectedQuestionsPanel>
          <PanelHeader>
            <PanelTitle>📝 Đã chọn ({selectedQuestionIds.length})</PanelTitle>
            <ClearAllButton onClick={handleClearAllSelected}>
              Bỏ chọn tất cả
            </ClearAllButton>
          </PanelHeader>
          
          {selectedQuestionIds.map(id => {
            const question = questions.find((q: any) => q.id === id);
            if (!question) return null;
            
            const questionType = questionTypes.find((t: any) => t.id === question.question_type_id);
            
            return (
              <SelectedQuestionItem key={id}>
                <QuestionInfo>
                  <strong>ID {id}</strong> - {questionType?.type_name || 'N/A'}
                </QuestionInfo>
                <RemoveButton onClick={() => handleRemoveSelected(id)}>
                  ×
                </RemoveButton>
              </SelectedQuestionItem>
            );
          })}
        </SelectedQuestionsPanel>
      )}

      {/* Floating Add to Exam Button */}
      {selectedQuestionIds.length > 0 && (
        <FloatingAddButton onClick={handleOpenAddToExamModal}>
          ➕ Thêm vào đề thi
          <ButtonBadge>{selectedQuestionIds.length}</ButtonBadge>
        </FloatingAddButton>
      )}

      {/* Floating Delete Button */}
      {selectedQuestionIds.length > 0 && (
        <FloatingDeleteButton onClick={handleBulkDeleteClick} disabled={isBulkDeleting}>
          🗑️ Xóa câu hỏi
          <ButtonBadge>{selectedQuestionIds.length}</ButtonBadge>
        </FloatingDeleteButton>
      )}

      {/* Undo Toast */}
      {lastDeletedBatch && lastDeletedBatch.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          right: 40,
          background: '#111827',
          color: 'white',
          padding: '14px 16px',
          borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          minWidth: 340,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Đã xóa {lastDeletedBatch.length} câu hỏi</div>
            <div style={{ fontSize: 12, color: '#d1d5db', marginBottom: 8 }}>Tự ẩn sau {Math.ceil(undoCountdown / 1000)}s</div>
            <div style={{ height: 6, background: '#1f2937', borderRadius: 9999, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.max(0, Math.min(100, (undoCountdown / UNDO_DURATION) * 100))}%`,
                background: '#10b981',
                transition: 'width 100ms linear',
              }} />
            </div>
          </div>
          <button onClick={handleUndoLastDelete} style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: 'none',
            background: '#10b981',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>Hoàn tác</button>
          <button onClick={() => setLastDeletedBatch(null)} style={{
            padding: '8px 10px',
            borderRadius: 8,
            border: 'none',
            background: '#374151',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>Ẩn</button>
        </div>
      )}

      {/* Add to Exam Modal */}
      <Modal isOpen={showAddToExamModal} toggle={() => setShowAddToExamModal(false)} size="md">
        <ModalHeader toggle={() => setShowAddToExamModal(false)}>
          📚 Chọn đề thi để thêm {selectedQuestionIds.length} câu hỏi
        </ModalHeader>
        <ModalBody>
          <ExamModalContent>
            {draftExams.length === 0 ? (
              <EmptyState>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                <p>Không có đề thi nào ở trạng thái Draft.</p>
                <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
                  Hãy tạo đề thi mới với status "Bản nháp" trước.
                </p>
              </EmptyState>
            ) : (
              draftExams.map((exam: any) => (
                <ExamCard key={exam.id} onClick={() => handleAddToExam(exam.id, exam.title)}>
                  <ExamCardTitle>{exam.title}</ExamCardTitle>
                  <ExamCardMeta>
                    <span>
                      📋 {exam.total_questions || 0} câu
                    </span>
                    <span>
                      ⏱️ {exam.duration} phút
                    </span>
                    {exam.applicable_classes && (
                      <span>
                        🎓 {exam.applicable_classes}
                      </span>
                    )}
                  </ExamCardMeta>
                </ExamCard>
              ))
            )}
          </ExamModalContent>
        </ModalBody>
        <ModalFooter>
          <SecondaryButton onClick={() => setShowAddToExamModal(false)}>
            Hủy
          </SecondaryButton>
        </ModalFooter>
      </Modal>
    </QuestionsContainer>
  );
};

export default QuestionsManage;

